/**
 * Sticky Shopping Footer — Page version.
 *
 * The product-template sticky footer owns a product form. On a page there is no
 * contextual product; the buy box is a `home-bundle-switcher` whose active panel
 * is either a bundle-builder or an async-injected single-product PDP, each with
 * its own variant, selling plan and (for bundles) flavor line-item properties.
 *
 * So this bar is a PROXY. It never builds a cart payload of its own:
 *   - It mirrors the active panel's live title / image / price from the DOM.
 *   - Its add-to-cart click forwards to the active panel's REAL button, so the
 *     exact current selection (flavors, variant, subscription plan) is added.
 *
 * Visibility: appears after a scroll threshold and hides over the footer. The bar
 * shows for both sides at all times; on the bundle side, an incomplete bundle is
 * surfaced as a greyed-out "Select N more boxes" button (mirrored from the
 * bundle's real button) rather than by hiding the bar.
 */
class StickyShoppingFooterPage extends HTMLElement {
  connectedCallback() {
    this.switcher = document.querySelector('home-bundle-switcher');
    // Nothing to proxy — leave the bar hidden.
    if (!this.switcher) return;

    this.abort = new AbortController();
    this.pastTrigger = false;
    this.inFooter = false;

    this.atcButton = this.querySelector('[data-page-footer-atc]');
    this.atcLabel = this.querySelector('[data-page-footer-atc-label]');
    this.titleEl = this.querySelector('[data-page-footer-title]');
    this.priceEl = this.querySelector('[data-page-footer-price]');
    this.imageEl = this.querySelector('[data-page-footer-image]');

    this.#wireClicks();
    this.#wireScrollTrigger();
    this.#wireBundleCompletion();
    this.#wireDomMirror();

    this.#sync();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.domObserver?.disconnect();
  }

  /** The currently visible buy-box panel inside the switcher. */
  get activePanel() {
    return this.switcher.querySelector('[data-bundle-panel]:not([hidden])');
  }

  /** The real add-to-cart / submit button inside the active panel. */
  #realAddToCart() {
    const panel = this.activePanel;
    if (!panel) return null;
    return (
      panel.querySelector('[data-bundle-add-to-cart-button]') ||
      panel.querySelector('[ref="addToCartButton"]') ||
      panel.querySelector('button[name="add"]') ||
      panel.querySelector('.add-to-cart-button') ||
      panel.querySelector('button[type="submit"]')
    );
  }

  #wireClicks() {
    this.addEventListener(
      'click',
      (event) => {
        const onAtc = Boolean(event.target.closest('[data-page-footer-atc]'));
        const real = this.#realAddToCart();

        // Add to cart only when the ATC button itself is clicked and ready.
        if (onAtc && real && !real.disabled) {
          event.preventDefault();
          this.#addToCart(real);
          return;
        }

        // Anything else on the bar — the greyed "Select N more boxes" button or
        // the product info — bounces the user up to the builder to keep shopping.
        event.preventDefault();
        this.#scrollToBuyBox();
      },
      { signal: this.abort.signal }
    );
  }

  #addToCart(real) {
    // The real button lives in the buy box, which is scrolled off-screen by the
    // time this bar shows — so its own fly-to-cart would launch from out of view.
    // Suppress it for this click and replay the animation from the visible bar.
    const realComponent = real.closest('add-to-cart-component');
    const prevFlag = realComponent?.getAttribute('data-add-to-cart-animation');
    realComponent?.setAttribute('data-add-to-cart-animation', 'false');

    // Forwarding the click runs the panel's own handler, which carries the
    // current variant, selling plan and bundle flavor properties.
    real.click();

    if (realComponent) {
      if (prevFlag == null) realComponent.removeAttribute('data-add-to-cart-animation');
      else realComponent.setAttribute('data-add-to-cart-animation', prevFlag);
    }

    this.#flyToCart();
  }

  /** Smooth-scroll up to the builder / buy box so the shopper can keep building. */
  #scrollToBuyBox() {
    const panel = this.activePanel;
    const target =
      (panel && panel.querySelector('bundle-builder-component, bundle-builder-flex-component')) ||
      panel ||
      this.switcher;
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.scrollY - headerOffset();
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  /**
   * Replays the theme's "fly to cart" animation from this bar's button, reusing
   * the global <fly-to-cart> element defined by product-form.js.
   */
  #flyToCart() {
    if (!customElements.get('fly-to-cart')) return;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image =
      this.imageEl && !this.imageEl.hidden ? this.imageEl.currentSrc || this.imageEl.src : null;
    if (!cartIcon || !image) return;

    const fly = document.createElement('fly-to-cart');
    fly.style.setProperty('background-image', `url(${image})`);
    fly.source = this.atcButton;
    fly.destination = cartIcon;
    document.body.appendChild(fly);
  }

  #wireScrollTrigger() {
    // Resolve the footer region once. We hide the bar once the footer's top
    // reaches the viewport and keep it hidden to the page bottom — computed live
    // on scroll rather than via IntersectionObserver, which stops firing once the
    // footer is fully past the top (that made the bar reappear at the very bottom).
    this.footerEl =
      document.querySelector('.shopify-section-group-footer-group') ||
      document.querySelector('footer') ||
      document.querySelector('[class*="footer"]:not(.sticky-shopping-footer)');

    const onScroll = () => {
      // Show only once the shopper has scrolled PAST the buy box — a duplicate
      // add-to-cart bar is noise while the real one is still on screen. The
      // switcher's height changes as the single panel's PDP loads in, so this is
      // measured live on every scroll rather than cached.
      const past = this.switcher.getBoundingClientRect().bottom <= 0;
      const inFooter = this.footerEl
        ? this.footerEl.getBoundingClientRect().top <= window.innerHeight
        : false;
      if (past !== this.pastTrigger || inFooter !== this.inFooter) {
        this.pastTrigger = past;
        this.inFooter = inFooter;
        this.#updateVisibility();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true, signal: this.abort.signal });
    window.addEventListener('resize', onScroll, { passive: true, signal: this.abort.signal });
    onScroll();
  }

  #wireBundleCompletion() {
    // The bundle's real button toggles disabled + label ("Select N more boxes"
    // ↔ "Add to Gym Bag") as boxes fill. Refresh the bar's mirrored button when
    // that happens — the bar stays visible either way.
    document.addEventListener(
      'bundle:completion-changed',
      () => this.#scheduleSync(),
      { signal: this.abort.signal }
    );
  }

  /**
   * Re-mirror the bar whenever the buy box changes: panel switches, async PDP
   * injection, flavor selection, subscription toggles and price updates all show
   * up as DOM mutations inside the switcher.
   */
  #wireDomMirror() {
    this.domObserver = new MutationObserver(() => this.#scheduleSync());
    this.domObserver.observe(this.switcher, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['hidden', 'data-active-side', 'class', 'disabled', 'src', 'value'],
    });
  }

  #scheduleSync() {
    if (this.syncQueued) return;
    this.syncQueued = true;
    requestAnimationFrame(() => {
      this.syncQueued = false;
      this.#sync();
    });
  }

  #sync() {
    const panel = this.activePanel;
    if (!panel) return;

    // Title
    const titleNode = panel.querySelector('[ref="productTitleLink"], h1, .product-title');
    if (this.titleEl && titleNode) this.titleEl.textContent = titleNode.textContent.trim();

    // Image
    const img = panel.querySelector('.product-information__media img') || panel.querySelector('img');
    if (this.imageEl) {
      if (img && img.currentSrc) {
        this.imageEl.src = img.currentSrc;
        this.imageEl.alt = img.alt || '';
        this.imageEl.hidden = false;
      } else {
        this.imageEl.hidden = true;
      }
    }

    // Price — clone whatever the panel currently renders so formatting/compare
    // pricing/subscription discounts come along for free. Degrade gracefully.
    if (this.priceEl) {
      const priceNode =
        panel.querySelector('.bundle-builder-block__price') ||
        panel.querySelector('[ref="priceContainer"]') ||
        panel.querySelector('.price__container') ||
        panel.querySelector('.price');
      if (priceNode && !priceNode.hidden && priceNode.textContent.trim()) {
        this.priceEl.innerHTML = priceNode.innerHTML;
        this.priceEl.hidden = false;
      } else {
        this.priceEl.hidden = true;
      }
    }

    // Mirror the real button's label + disabled state (e.g. "Select 2 more
    // boxes" / "Add to Gym Bag" / sold out).
    const real = this.#realAddToCart();
    // Visually mark (not natively disable) so an incomplete click can still
    // bounce the user back up to the builder.
    if (this.atcButton) this.atcButton.classList.toggle('is-incomplete', Boolean(real?.disabled));
    if (this.atcLabel && real) {
      const realLabel =
        real.querySelector('.add-to-cart-text__content, .bundle-builder-block__button-text') || real;
      let text = realLabel.textContent.trim();
      // Compact label on mobile: icon + "Add • $25.50/Box".
      if (text && window.matchMedia('(max-width: 749px)').matches) {
        text = text.replace(/^Add to Gym Bag\b/, 'Add');
      }
      if (text) this.atcLabel.textContent = text;
    }

    this.#updateVisibility();
  }

  #updateVisibility() {
    const visible = this.pastTrigger && !this.inFooter;
    if (visible) this.removeAttribute('hidden');
    else this.setAttribute('hidden', '');
  }
}

/** Pixels to keep clear at the top when scrolling, accounting for a sticky header. */
function headerOffset() {
  const header = document.querySelector('#header-component') || document.querySelector('.header');
  if (!header) return 16;
  const declared = getComputedStyle(header).getPropertyValue('--header-height').trim();
  const height = parseInt(declared, 10) || header.getBoundingClientRect().height || 0;
  return height + 16;
}

if (!customElements.get('sticky-shopping-footer-page')) {
  customElements.define('sticky-shopping-footer-page', StickyShoppingFooterPage);
}
